import { useState, useEffect, useMemo } from "react";
import Button from "components/Elements/Button/Button";
import getFeatureBySystemName from "utils/getFeatures";
import Icon from "components/Elements/Icon/Icon.tsx";
import FeaturesPage from "./Contents/FeaturesPage";
import Image from "components/Elements/Image/Image.tsx";
import queryString from "query-string";
import useMediaQueries from "hooks/useMediaQueries.js";
import BlackFridayPromoElem from "components/Reusable/BlackFriday/BlackFridayPromoElem";
import CardsContainer from "./Contents/CardsContainer";
import { useLocation } from "react-router-dom";
import { useContent } from "contexts/contentContext/contentContext.js";
import { nisList, eurList } from "utils/jumbomailSettings.js";
import { LinkToNewTab } from "components/Elements/Link/Link";
import { useUser } from "contexts/userContext/userContext";
import { ButtonAccessability } from "components/Elements/Button/Button";

import "./Plans.scss";

const Plans = () => {
  const {
    getTextByKey,
    getLanguage,
    getBlackFridayTime,
    getIsBlackFriday,
    getDirection,
    getHtmlByKey,
    getBasicSettingsData,
  } = useContent();
  const { getUserData } = useUser();
  const { mobileCondition } = useMediaQueries();
  const [isYearly, setIsYearly] = useState(true);
  const [clickedCard, setClickedCard] = useState("free");
  const [prices, setPrices] = useState({});
  const [coupons, setCoupons] = useState({});
  const location = useLocation();
  const displayExtraStorage = !getUserData()?.subUserRole;
  const [extraStorageFeature, setExtraStorageFeature] = useState(null);

  // handle scroll to fetures list
  const scrollToFeaturesUrl = queryString.parse(location.search);
  const scrollToFeatures =
    scrollToFeaturesUrl && scrollToFeaturesUrl.r ? true : false;

  const goToFeatures = (fromUrl: boolean) => {
    //scroll to center of plans-features-id
    const numToScroll = 70;
    let headerPos =
      document.querySelector("#plans-features-id")?.getBoundingClientRect()
        .top || 0;
    if (fromUrl) {
      headerPos = headerPos + 320;
    }
    headerPos > 0 && window.scrollBy(0, headerPos - numToScroll);
    //element.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    if (scrollToFeatures && !mobileCondition) {
      goToFeatures(true);
    }
  }, [scrollToFeatures]);

  const currentUserCurrency = useMemo(() => {
    const countryName =
      getUserData().UserCountry || getBasicSettingsData().userCountry;
    if (nisList.includes(countryName)) return "nis";
    if (eurList.includes(countryName)) return "eur";
    return "usd";
  }, [getUserData(), getBasicSettingsData()]);

  const convertPriceOfStorage = () => {
    const constText = "extra-storage-plans-page-text";
    const text = getTextByKey(constText);
    const currencyPrice =
      currentUserCurrency === "nis"
        ? "ILS"
        : currentUserCurrency === "eur"
        ? "EUR"
        : "USD";
    const price = extraStorageFeature ? extraStorageFeature[currencyPrice] : 0;
    const currency = currentUserCurrency;
    //replace #price# with the price and currency
    const newText =
      price > 0 ? text.replace("#price#", `${price}${currency}`) : "";
    return newText;
  };

  useEffect(() => {
    getFeatureBySystemName(
      "extra-storage",
      getLanguage(),
      setExtraStorageFeature
    );
  }, [getLanguage()]);

  return (
    <div className="plans-page-container">
      <div className="cards-sction">
        <div className="plans-page-header">
          <h1>{getTextByKey("plans-title")}</h1>
          <div
            className={`relative-wrapper${
              getBlackFridayTime() ? " black-friday-margin" : ""
            }`}
          >
            <div
              className={`yearly-monthly-toggle ${
                getLanguage() === "ru" || getLanguage() === "it"
                  ? "ru-it-lang"
                  : ""
              }`}
            >
              <div
                onClick={() => setIsYearly(false)}
                className={`monthly-side side ${
                  !isYearly ? "side-active" : ""
                }`}
              >
                <span className="text">{getTextByKey("monthly")}</span>
              </div>
              <div
                onClick={() => setIsYearly(true)}
                className={`yearly-side side ${isYearly ? "side-active" : ""}`}
              >
                <span className="text">
                  {getTextByKey("yearly")}{" "}
                  <span className="save-percents">
                    ({getTextByKey("save-17-percents")})
                  </span>
                </span>{" "}
              </div>
            </div>

            {getBlackFridayTime() ? (
              !getIsBlackFriday() ? (
                <BlackFridayPromoElem
                  // @ts-ignore
                  customImageName={
                    getDirection() === "rtl"
                      ? "black-friday/label_sale_flip"
                      : null
                  }
                />
              ) : (
                <div className="black-friday-promo">
                  <Image name="black-friday/logo_lable" ext="svg" />
                </div>
              )
            ) : null}
          </div>
          <div className="sub-title">{getHtmlByKey("plans-page-subtitle")}</div>
        </div>
        <CardsContainer
          isYearly={isYearly}
          setClickedCard={setClickedCard}
          setPrices={setPrices}
          setCoupons={setCoupons}
        />
        <div className="additional-info-picker-forms">
          <div>
            <Image
              name="logo-picker-text"
              ext="svg"
              title="JUMBOpicker"
              alt="JUMBOpicker"
            />
            <Image
              name="logo-forms-text"
              ext="svg"
              title="JUMBOforms"
              alt="JUMBOforms"
            />
          </div>
          {getTextByKey("included-in-plans")}
        </div>
        {displayExtraStorage && (
          <div className="extra-storage">
            {convertPriceOfStorage()}
            {/* @ts-ignore */}
            <LinkToNewTab page={"purchase/extra-storage"} isNewTab>
              <Button blank className="new-button">
                {getTextByKey("purchase")}
              </Button>
            </LinkToNewTab>
          </div>
        )}
        <ButtonAccessability
          buttonClassName="see-all-features"
          onClick={() => goToFeatures(false)}
          title={getTextByKey("see-all-features")}
        >
          <span className="see-all-features-text">
            {getTextByKey("see-all-features")}
          </span>
          <div className="see-all-features-icon">
            <Icon name="arrow-long-left" />
          </div>
        </ButtonAccessability>
        <Image
          className="clouds-img"
          name="plans/clouds-footer2"
          ext="png"
          alt=""
          title=""
        />
      </div>
      <FeaturesPage
        isYearly={isYearly}
        clickedCard={clickedCard}
        prices={prices}
        coupons={coupons}
      />
    </div>
  );
};

export default Plans;
